/**
 * Market / region utility functions.
 * All currency formatting and region-specific labels go here.
 * market: 'IN' (default, India ₹) | 'US' (United States $)
 */

export const isUS = (market) => market === 'US';

/**
 * Format a numeric value as currency for the given market.
 * India: ₹ with Cr / L / K notation
 * US:   $ with B / M / K notation
 */
export const formatCurrency = (value, market = 'IN') => {
  if (value === null || value === undefined) return '—';
  const v = Number(value);
  if (isNaN(v)) return '—';

  if (market === 'US') {
    if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
    if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
    return `$${v.toFixed(2)}`;
  }

  // India default
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (Math.abs(v) >= 100000)   return `₹${(v / 100000).toFixed(2)} L`;
  if (Math.abs(v) >= 1000)     return `₹${(v / 1000).toFixed(2)} K`;
  return `₹${v.toFixed(2)}`;
};

/** Spaced version used in ConsolidatedDashboard etc. */
export const formatCurrencySpaced = (value, market = 'IN') => {
  if (value === null || value === undefined) return '—';
  const v = Number(value);
  if (isNaN(v)) return '—';

  if (market === 'US') {
    if (Math.abs(v) >= 1e9) return `$ ${(v / 1e9).toFixed(2)}B`;
    if (Math.abs(v) >= 1e6) return `$ ${(v / 1e6).toFixed(2)}M`;
    if (Math.abs(v) >= 1e3) return `$ ${(v / 1e3).toFixed(2)}K`;
    return `$ ${v.toFixed(2)}`;
  }

  if (Math.abs(v) >= 10000000) return `₹ ${(v / 10000000).toFixed(2)} Cr`;
  if (Math.abs(v) >= 100000)   return `₹ ${(v / 100000).toFixed(2)} L`;
  if (Math.abs(v) >= 1000)     return `₹ ${(v / 1000).toFixed(1)} K`;
  return `₹ ${v.toLocaleString('en-IN')}`;
};

/** Masked value for privacy toggle */
export const maskCurrency = (market = 'IN') =>
  market === 'US' ? '$ ••••••' : '₹ ••••••';

/** Currency symbol only */
export const getCurrencySymbol = (market = 'IN') =>
  market === 'US' ? '$' : '₹';

/** Exchange labels */
export const getExchangeLabel = (market = 'IN') =>
  market === 'US' ? 'NYSE / NASDAQ' : 'NSE / BSE';

/** Market benchmark indices */
export const getMarketIndices = (market = 'IN') =>
  market === 'US'
    ? ['S&P 500', 'NASDAQ', 'Dow Jones']
    : ['NIFTY 50', 'SENSEX', 'NIFTY MIDCAP 50'];

/** Tax terminology */
export const getTaxLabels = (market = 'IN') =>
  market === 'US'
    ? { short: 'Short-Term', long: 'Long-Term', shortRate: '37%', longRate: '20%', exemption: null }
    : { short: 'STCG', long: 'LTCG', shortRate: '20%', longRate: '12.5%', exemption: 125000 };

/** Locale for number formatting */
export const getLocale = (market = 'IN') =>
  market === 'US' ? 'en-US' : 'en-IN';
